import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import useNow from 'hooks/useNow';
import { simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';

import { isPortalWorkflow, PORTAL_DISCOVERY_API_NAME, sourceSystemLabel, VALUE_NAMES } from './constants';
import {
  categorySegments,
  isOpenSweep,
  LIST_STATUS_DISPLAY,
  overallListStatus,
  progressSummary,
} from './referralProgress';
import { Tone, TONE_STYLES } from './StatusBadge';

const columnHelper = createColumnHelper<Workflow>();

// "12 min" under an hour; "1 hr 3 mins" beyond. Coarse on purpose — it's an at-a-glance age.
// Shared with the referral page's header subline ("… - 12 min ago").
export function elapsed(date: Date, now: Date): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min${rest === 1 ? '' : 's'}`;
}

// Its own component so the clock ticks independently of data fetches (OLIO-2602):
// server timestamps stay the source of truth; the tick only advances the display.
function ElapsedCell({ created }: { created: Date | null }) {
  const now = useNow(30_000);
  if (!created) return <>—</>;
  return (
    <TimeCell>
      <Duration>{elapsed(created, now)}</Duration>
      <Timestamp>{simpleDateAtTime(created)}</Timestamp>
    </TimeCell>
  );
}

// A discovery sweep is anchored to a portal, not a referral, so it has no patient and no
// insurance. It shares this table with retrieval runs (OLIO-2758), so the patient-shaped columns
// must not present a sweep as a referral with missing data — `patientName` in particular falls
// back to the literal 'New referral'.
function isDiscovery(workflow: Workflow): boolean {
  return workflow.definition.apiName === PORTAL_DISCOVERY_API_NAME;
}

// The portal a run concerns. Every portal run carries source_system — it's the only value a
// sweep has, and retrieval runs carry it alongside their referral data — so this earns its own
// column rather than being crammed into the patient cell.
function portalLabel(workflow: Workflow): string {
  const sourceSystem = workflow.valueFor('source_system')?.value;
  return sourceSystem ? sourceSystemLabel(sourceSystem) : '';
}

// What the subject cell says when there's no patient name. A sweep has no patient at all; a
// portal retrieval run has none *yet* (extraction fills it in later) and the bare 'New referral'
// fallback reads as a referral someone forgot to name.
//
// Deliberately NOT suffixed with the portal ("Portal referral — Faux-Port"): the Portal column
// right next door already says which portal, so the suffix was the same word twice per row. The
// detail page still suffixes, because it has no Portal column to lean on.
//
// Only for PORTAL runs: these columns are shared with the inbound-referrals inbox (a
// referral-intake run genuinely is a new referral, so it keeps Workflow#patientName's wording).
function subjectLabel(workflow: Workflow): string {
  if (workflow.hasPatientName) return workflow.patientName;
  // An open sweep pins to the top of the list (OLIO-2814); the suffix says why it's first so
  // the position doesn't read as an ordering quirk. Copy only — UX may replace it with a
  // dedicated affordance later. Drops with the pin once the sweep is handed off.
  if (isDiscovery(workflow)) return isOpenSweep(workflow) ? 'Portal sweep — needs review' : 'Portal sweep';
  if (!isPortalWorkflow(workflow.definition.apiName)) return workflow.patientName;
  return 'Portal referral';
}

const inboundReferralsTableColumns = () => [
  columnHelper.accessor((row) => subjectLabel(row), {
    id: 'patientName',
    // "Subject", not "Patient": a third of the rows aren't patients — a sweep's subject is the
    // portal itself. (Matches FHIR's sense of the word: what the resource is about.)
    header: 'Subject',
    // Muted rather than link-blue when it's a stand-in rather than a person's name. The row
    // still navigates either way.
    cell: (info) =>
      info.row.original.hasPatientName ? (
        <PatientLink>{info.getValue()}</PatientLink>
      ) : (
        <SweepLabel data-testid='workflow-type'>{info.getValue()}</SweepLabel>
      ),
    size: 200,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => portalLabel(row), {
    id: 'portal',
    header: 'Portal',
    // Useful for both types: which portal this work concerns.
    cell: (info) => info.getValue() || '—',
    size: 110,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.valueFor(VALUE_NAMES.PLAN_TYPE)?.value ?? '', {
    id: 'planType',
    header: 'Plan Type',
    // Not applicable to a sweep (no patient, no insurance) — blank rather than a dash, so the
    // row doesn't read as a referral whose payer is merely unknown.
    cell: (info) => (isDiscovery(info.row.original) ? '' : info.getValue() || '—'),
    size: 120,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.valueFor(VALUE_NAMES.REFERRAL_SOURCE)?.value ?? '', {
    id: 'referralSource',
    header: 'Referral Source',
    cell: (info) => (isDiscovery(info.row.original) ? '' : info.getValue() || '—'),
    size: 150,
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.groupNames.join(', '), {
    id: 'snf',
    header: 'SNF',
    cell: (info) => (isDiscovery(info.row.original) ? '' : info.getValue() || '—'),
    size: 130,
    enableSorting: false,
  }),
  columnHelper.display({
    id: 'progress',
    header: 'Progress',
    size: 190,
    cell: (info) => {
      const workflow = info.row.original;
      const { index, total, categoryName } = progressSummary(workflow);
      return (
        <ProgressCell>
          <Bar data-testid='progress-bar'>
            {categorySegments(workflow).map((segment) => (
              <Segment key={segment.key} $color={TONE_STYLES[LIST_STATUS_DISPLAY[segment.status].tone].accent} />
            ))}
          </Bar>
          <ProgressLabel>{total > 0 ? `${index} of ${total} – ${categoryName}` : '—'}</ProgressLabel>
        </ProgressCell>
      );
    },
  }),
  columnHelper.display({
    id: 'currentStatus',
    header: 'Current Status',
    size: 140,
    cell: (info) => {
      const status = overallListStatus(info.row.original);
      const display = LIST_STATUS_DISPLAY[status];
      return (
        <Pill $tone={display.tone} data-testid='list-status'>
          {status === 'processing' && <AISparkleIcon width={12} height={12} color={TONE_STYLES[display.tone].color} />}
          {display.label}
        </Pill>
      );
    },
  }),
  columnHelper.accessor((row) => row.createdAt, {
    id: 'timeElapsed',
    header: 'Time Elapsed',
    size: 150,
    enableSorting: false,
    cell: (info) => <ElapsedCell created={info.getValue() as Date | null} />,
  }),
];

export default inboundReferralsTableColumns;

const PatientLink = styled.span`
  font-weight: 500;
  color: ${colors.blue100};
`;

// A sweep's identity cell. Single-line so a discovery row is the same height as a referral row,
// and muted/italic rather than link-blue because there's no patient behind it — the row still
// navigates, but the text isn't naming a person.
const SweepLabel = styled.span`
  font-weight: 500;
  font-style: italic;
  color: ${colors.black50};
`;

const ProgressCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Bar = styled.div`
  display: flex;
  gap: 4px;
`;

const Segment = styled.div<{ $color: string }>`
  height: 9px;
  flex: 1;
  border-radius: 3px;
  background: ${({ $color }) => $color};
`;

const ProgressLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;

const Pill = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $tone }) => TONE_STYLES[$tone].background};
  color: ${({ $tone }) => TONE_STYLES[$tone].color};
`;

const TimeCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const Duration = styled.span`
  color: ${colors.black100};
`;

const Timestamp = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;
